import classnames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';

import Icon from 'dpl/common/components/Icon';
import { ANALYTICS_VIEWS } from 'dpl/constants/analytics';

import { usePuppyData } from '../../hooks/usePuppyData';
import PriceBreakdown from '../PriceBreakdown';
import RequestInfoButton from '../RequestInfoButton';
import BuyNowPayLaterDetails from '../BuyNowPayLaterDetails';

const CTA_VARIANTS = {
  base: 'font-16 fw-medium pv3 w-100 br2 ba tc',
  primary: 'bg-white b--stone-700 stone-700',
  secondary: 'bg-subtle-gray b--subtle-gray stone-700'
};

export default function SummaryDetails({ className, ctaVariants, analytics }) {
  const { puppy, litter, breeder } = usePuppyData();

  const ctaButtonVariants = { ...CTA_VARIANTS, ...ctaVariants };

  return (
    <div
      className={classnames(
        'SummaryDetails flex flex-column br4 overflow-hidden ba b--black-10',
        className
      )}
    >
      <div className="SummaryDetails__header flex items-center justify-between pv6 ph6 bg-sand">
        <h4 className="heading-4">
          <span className="db">{puppy.ready_date_description}</span>
        </h4>
      </div>
      <div className="SummaryDetails__content pv6 ph6">
        <Subheading iconName="fetch-payment">Pricing</Subheading>
        {puppy.price_description ? (
          <>
            <p className="secondary mt2">{puppy.price_description}</p>
            <PriceBreakdown
              deposit={puppy.deposit}
              description={puppy.price_breakdown_description}
              puppyPayment={puppy.puppy_payment}
              totalPayment={puppy.total_payment}
              analytics={analytics}
            />
            {puppy.bnpl_short_description && (
              <p className="font-14 mt4">
                {puppy.bnpl_short_description}&nbsp;
                <BuyNowPayLaterDetails />
              </p>
            )}
          </>
        ) : (
          <p className="secondary mt2">
            Request info to get more details about {puppy.name}&apos;s price.
          </p>
        )}
        <div className="bt b--black-10 mt6 pt6">
          <Subheading iconName="fetch-car">
            Getting {puppy.name} home
          </Subheading>
          <p className="secondary mt2">{puppy.transportation_description}</p>
        </div>
      </div>
      {!puppy.reserved && (
        <div className="SummaryDetails__footer flex-auto flex items-end pv6 ph6">
          <RequestInfoButton
            analytics={analytics}
            breederProfileId={breeder.id}
            litterId={litter.id}
            puppyId={puppy.id}
            inquirySource={ANALYTICS_VIEWS.PUPPY_PROFILE}
            variants={ctaButtonVariants}
          />
        </div>
      )}
    </div>
  );
}

SummaryDetails.propTypes = {
  analytics: PropTypes.shape({
    view: PropTypes.string.isRequired,
    view_feature: PropTypes.string.isRequired,
    view_feature_location: PropTypes.string.isRequired
  }).isRequired,
  className: PropTypes.string,
  ctaVariants: PropTypes.shape({
    base: PropTypes.string,
    primary: PropTypes.string,
    secondary: PropTypes.string,
    loading: PropTypes.string
  })
};

SummaryDetails.defaultProps = {
  className: null,
  ctaVariants: CTA_VARIANTS
};

function Subheading({ iconName, children }) {
  return (
    <div className="flex items-center">
      <Icon className="mr2" name={iconName} />
      <h5 className="font-sans font-16 lh-1">{children}</h5>
    </div>
  );
}

Subheading.propTypes = {
  iconName: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired
};
